import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, PageHeader } from './AdminUI';
import { getImageUrl } from '../../config/api';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';

const CaseStudiesManagement = () => {
    const imageInputRef = useRef(null);
    const { apiCall } = useAuth();
    const [caseStudies, setCaseStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCase, setEditingCase] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        client: '',
        industry: '',
        duration: '',
        challenge: '',
        solution: '',
        results: '',
        image: 'images/case-studies/default.jpg',
        thumbnail: 'images/case-studies/default.jpg',
        tags: [],
        featured: false,
        published: true
    });
    const [tagInput, setTagInput] = useState('');

    useEffect(() => {
        fetchCaseStudies();
    }, []);

    const fetchCaseStudies = async () => {
        try {
            const response = await apiCall('/case-studies');
            const data = await response.json();
            // Ensure we always set an array
            const studies = Array.isArray(data.caseStudies) ? data.caseStudies :
                           Array.isArray(data) ? data : [];
            setCaseStudies(studies);
        } catch (error) {
            console.error('Error fetching case studies:', error);
            setCaseStudies([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingCase ? `/case-studies/${editingCase.id}` : '/case-studies';
            const method = editingCase ? 'PUT' : 'POST';

            let body;
            if (imageFile) {
                const fd = new FormData();
                fd.append('title', formData.title);
                fd.append('slug', formData.slug);
                fd.append('client', formData.client);
                fd.append('industry', formData.industry);
                fd.append('duration', formData.duration);
                fd.append('challenge', formData.challenge);
                fd.append('solution', formData.solution);
                fd.append('results', formData.results);
                fd.append('tags', JSON.stringify(formData.tags));
                fd.append('featured', String(formData.featured));
                fd.append('published', String(formData.published));
                fd.append('image', imageFile);
                body = fd;
            } else {
                body = JSON.stringify({ ...formData, thumbnail: formData.thumbnail || formData.image });
            }

            const response = await apiCall(url, { method, body });

            if (response.ok) {
                await fetchCaseStudies();
                handleCloseModal();
                alertSuccess('Saved', 'Case study saved successfully.');
            } else {
                const result = await response.json();
                alertError('Error', result.message || 'Error saving case study');
            }
        } catch (error) {
            console.error('Error saving case study:', error);
            alertError('Error', 'Error saving case study');
        }
    };

    const handleEdit = (caseStudy) => {
        setEditingCase(caseStudy);
        setImageFile(null);
        setFormData({
            title: caseStudy.title,
            slug: caseStudy.slug,
            client: caseStudy.client,
            industry: caseStudy.industry,
            duration: caseStudy.duration,
            challenge: caseStudy.challenge,
            solution: caseStudy.solution,
            results: caseStudy.results,
            image: caseStudy.image,
            thumbnail: caseStudy.thumbnail || caseStudy.image,
            tags: Array.isArray(caseStudy.tags) ? caseStudy.tags : [],
            featured: caseStudy.featured,
            published: caseStudy.published
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const { isConfirmed } = await confirmDelete({ title: 'Delete case study?', text: 'Are you sure? This cannot be undone.' });
        if (!isConfirmed) return;

        try {
            const response = await apiCall(`/case-studies/${id}`, { method: 'DELETE' });
            if (response.ok) {
                await fetchCaseStudies();
                alertSuccess('Deleted', 'Case study deleted.');
            } else {
                alertError('Error', 'Error deleting case study');
            }
        } catch (error) {
            console.error('Error deleting case study:', error);
            alertError('Error', 'Error deleting case study');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCase(null);
        setFormData({
            title: '',
            slug: '',
            client: '',
            industry: '',
            duration: '',
            challenge: '',
            solution: '',
            results: '',
            image: 'images/case-studies/default.jpg',
            thumbnail: 'images/case-studies/default.jpg',
            tags: [],
            featured: false,
            published: true
        });
        setImageFile(null);
        setTagInput('');
    };

    const imagePreviewUrl = imageFile
        ? URL.createObjectURL(imageFile)
        : getImageUrl(formData.image);

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setFormData({ ...formData, title, slug: generateSlug(title) });
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
    };

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading case studies..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="Case Studies"
                    subtitle="Showcase your success stories"
                    action={
                    <button
                        onClick={() => setShowModal(true)}
                        className="btn btn-primary"
                    >
                        <i className="fa fa-plus"></i>
                        Add Case Study
                    </button>
                    }
                />

                <div className="admin-card-grid is-wide">
                    {caseStudies.length === 0 ? (
                        <div style={{ gridColumn: '1/-1' }}>
                            <EmptyState
                                icon="fa fa-folder-open"
                                title="No case studies yet"
                                text="Add your first case study to showcase project outcomes."
                            />
                        </div>
                    ) : (
                        caseStudies.map((caseStudy) => (
                            <div key={caseStudy.id} style={{
                                background: 'white',
                                borderRadius: '0',
                                overflow: 'hidden',
                                border: caseStudy.featured ? '2px solid #8b5cf6' : 'none'
                            }}>
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src={getImageUrl(caseStudy.thumbnail || caseStudy.image)}
                                        alt={caseStudy.title}
                                        style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="350" height="180"%3E%3Crect fill="%23ddd" width="350" height="180"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                        }}
                                    />
                                    {caseStudy.featured && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            right: '8px',
                                            background: '#8b5cf6',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '0',
                                            fontSize: '11px',
                                            fontWeight: '500'
                                        }}>
                                            Featured
                                        </div>
                                    )}
                                </div>
                                <div style={{ padding: '16px' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1a202c', marginBottom: '8px' }}>
                                        {caseStudy.title}
                                    </h3>
                                    <p style={{ fontSize: '13px', color: '#718096', marginBottom: '12px' }}>
                                        <i className="fa fa-building-o"></i> {caseStudy.client}
                                    </p>
                                    <p style={{ fontSize: '13px', color: '#718096', marginBottom: '16px', lineHeight: '1.5' }}>
                                        {caseStudy.challenge?.substring(0, 100)}...
                                    </p>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            onClick={() => handleEdit(caseStudy)}
                                            style={{
                                                flex: 1,
                                                padding: '8px',
                                                background: '#8b5cf6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <i className="fa fa-edit"></i> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(caseStudy.id)}
                                            style={{
                                                padding: '8px 12px',
                                                background: '#e53e3e',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <i className="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Modal */}
                {showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '0',
                            maxWidth: '800px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            padding: '32px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                                    {editingCase ? 'Edit Case Study' : 'Add Case Study'}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Title *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={handleTitleChange}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Slug *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.slug}
                                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Client *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.client}
                                            onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Industry *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.industry}
                                            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Duration
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.duration}
                                            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                            placeholder="e.g., 6 months"
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Case Study Image
                                        </label>
                                        <input
                                            ref={imageInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/gif,image/webp"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                setImageFile(file || null);
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {imagePreviewUrl && (
                                            <div style={{ marginTop: '12px' }}>
                                                <img
                                                    src={imagePreviewUrl}
                                                    alt="Preview"
                                                    style={{
                                                        maxWidth: '200px',
                                                        maxHeight: '160px',
                                                        objectFit: 'cover',
                                                        border: '1px solid #e2e8f0'
                                                    }}
                                                />
                                                {imageFile && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setImageFile(null);
                                                            if (imageInputRef.current) imageInputRef.current.value = '';
                                                        }}
                                                        style={{
                                                            display: 'block',
                                                            marginTop: '8px',
                                                            padding: '4px 12px',
                                                            background: '#e2e8f0',
                                                            border: 'none',
                                                            borderRadius: '0',
                                                            cursor: 'pointer',
                                                            fontSize: '13px'
                                                        }}
                                                    >
                                                        Remove image
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Challenge *
                                    </label>
                                    <textarea
                                        value={formData.challenge}
                                        onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                                        required
                                        rows={3}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                        placeholder="Describe the problem or challenge..."
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Solution *
                                    </label>
                                    <textarea
                                        value={formData.solution}
                                        onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                                        required
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                        placeholder="Describe the solution you provided..."
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Results *
                                    </label>
                                    <textarea
                                        value={formData.results}
                                        onChange={(e) => setFormData({ ...formData, results: e.target.value })}
                                        required
                                        rows={3}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                        placeholder="Describe the results and impact..."
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Tags
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                        <input
                                            type="text"
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                            style={{
                                                flex: 1,
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                            placeholder="Add tag and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTag}
                                            style={{
                                                padding: '10px 20px',
                                                background: '#8b5cf6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {formData.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                style={{
                                                    background: '#ede9fe',
                                                    color: '#7c3aed',
                                                    padding: '4px 12px',
                                                    borderRadius: '0',
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTag(tag)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed' }}
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.featured}
                                            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontWeight: '500', color: '#2d3748' }}>Featured case study</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '8px' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.published}
                                            onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontWeight: '500', color: '#2d3748' }}>Published</span>
                                    </label>
                                </div>

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        style={{
                                            padding: '12px 24px',
                                            background: '#e2e8f0',
                                            color: '#4a5568',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px 24px',
                                            background: '#8b5cf6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {editingCase ? 'Update' : 'Add'} Case Study
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default CaseStudiesManagement;
